import { Component, Suspense, lazy, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.js';
import { captureReactError, setSentryUser } from '@/lib/sentry.js';

// Layouts
import { AuthLayout } from '@/layouts/AuthLayout.js';
import { AppLayout } from '@/layouts/AppLayout.js';

// Auth pages
import { LoginPage } from '@/pages/auth/LoginPage.js';
import { InviteAcceptPage } from '@/pages/auth/InviteAcceptPage.js';

// Core pages
import { DashboardPage } from '@/pages/DashboardPage.js';
import { DisplayPage } from '@/pages/DisplayPage.js';
import { QueueDisplayPage } from '@/pages/QueueDisplayPage.js';
import { POSPage } from '@/pages/POSPage.js';
import { SalesHistoryPage } from '@/pages/sales/SalesHistoryPage.js';
import { CustomerDetailPage } from '@/pages/customers/CustomerDetailPage.js';
import { CustomerListPage } from '@/pages/customers/CustomerListPage.js';
import { SubscriptionsPage } from '@/pages/subscriptions/SubscriptionsPage.js';
import { InventoryListPage } from '@/pages/inventory/InventoryListPage.js';
import { InventoryBulkPage } from '@/pages/inventory/InventoryBulkPage.js';
import { PartsRequestsPage } from '@/pages/inventory/PartsRequestsPage.js';
import { InventoryItemPage } from '@/pages/inventory/InventoryItemPage.js';
import { PurchaseOrderListPage } from '@/pages/inventory/PurchaseOrderListPage.js';
import { PurchaseOrderDetailPage } from '@/pages/inventory/PurchaseOrderDetailPage.js';
import { CycleCountListPage } from '@/pages/inventory/CycleCountListPage.js';
import { CycleCountDetailPage } from '@/pages/inventory/CycleCountDetailPage.js';
import { TransferListPage } from '@/pages/inventory/TransferListPage.js';
import { TransferDetailPage } from '@/pages/inventory/TransferDetailPage.js';
import { RmaListPage } from '@/pages/inventory/RmaListPage.js';
import { RmaCreatePage } from '@/pages/inventory/RmaCreatePage.js';
import { RmaDetailPage } from '@/pages/inventory/RmaDetailPage.js';
import { DamageReportsPage } from '@/pages/inventory/DamageReportsPage.js';
import { SettingsPage } from '@/pages/settings/SettingsPage.js';
import { NotFoundPage } from '@/pages/NotFoundPage.js';
import { CheckoutPage } from '@/pages/tickets/CheckoutPage.js';
import { NewTicketPage } from '@/pages/tickets/NewTicketPage.js';
import { QuickTicketPage } from '@/pages/tickets/QuickTicketPage.js';
import { TicketDetailPage } from '@/pages/tickets/TicketDetailPage.js';
import { TicketWaiverRecordPage } from '@/pages/tickets/TicketWaiverRecordPage.js';
import { TicketQueuePage } from '@/pages/tickets/TicketQueuePage.js';
import { ReportsLayout } from '@/pages/reports/ReportsLayout.js';
import { ReportsDashboardPage } from '@/pages/reports/ReportsDashboardPage.js';
import { RevenueReportPage } from '@/pages/reports/RevenueReportPage.js';
import { TicketsReportPage } from '@/pages/reports/TicketsReportPage.js';
import { OutstandingBalancesReportPage } from '@/pages/reports/OutstandingBalancesReportPage.js';
import { TechniciansReportPage } from '@/pages/reports/TechniciansReportPage.js';
import { RepairsReportPage } from '@/pages/reports/RepairsReportPage.js';
import { InventoryReportPage } from '@/pages/reports/InventoryReportPage.js';
import { MultiStoreReportPage } from '@/pages/reports/MultiStoreReportPage.js';
import { EndOfDayReportPage } from '@/pages/reports/EndOfDayReportPage.js';
import { NpsReportPage } from '@/pages/reports/NpsReportPage.js';
import { LeaderboardPage } from '@/pages/reports/LeaderboardPage.js';
import { ProfitLossReportPage } from '@/pages/reports/ProfitLossReportPage.js';
import { CustomerLtvReportPage } from '@/pages/reports/CustomerLtvReportPage.js';
import { AcquisitionReportPage } from '@/pages/reports/AcquisitionReportPage.js';
import { CashFlowReportPage } from '@/pages/reports/CashFlowReportPage.js';
import { PartsForecastReportPage } from '@/pages/reports/PartsForecastReportPage.js';
import { ReferralsReportPage } from '@/pages/reports/ReferralsReportPage.js';
import { InsuranceReportPage } from '@/pages/reports/InsuranceReportPage.js';
import { UpsellReportPage } from '@/pages/reports/UpsellReportPage.js';
import { DiscountReportPage } from '@/pages/reports/DiscountReportPage.js';
import { TipsReportPage } from '@/pages/reports/TipsReportPage.js';
import { WalkInQueueReportPage } from '@/pages/reports/WalkInQueueReportPage.js';
import { RmaAndReworkReportPage } from '@/pages/reports/RmaAndReworkReportPage.js';
import { PartsRequestReportPage } from '@/pages/reports/PartsRequestReportPage.js';
import { ApprovalsPage } from '@/pages/ApprovalsPage.js';
import { MessagesPage } from '@/pages/messages/MessagesPage.js';
import { ReviewsPage } from '@/pages/reviews/ReviewsPage.js';
import { InquiriesListPage } from '@/pages/inquiries/InquiriesListPage.js';
import { NewInquiryPage } from '@/pages/inquiries/NewInquiryPage.js';
import { InquiryDetailPage } from '@/pages/inquiries/InquiryDetailPage.js';
import { postLoginPath } from '@/lib/post-login-path.js';
import { PhotoCapturePage } from '@/pages/photo/PhotoCapturePage.js';

// Customer portal
import { PortalLayout } from '@/layouts/PortalLayout.js';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage.js';
import {
  PortalForgotPasswordPage,
  PortalResetPasswordPage,
  PortalChangePasswordPage,
} from '@/pages/portal/PortalAuthPages.js';
import { PortalDashboardPage } from '@/pages/portal/PortalDashboardPage.js';
import { PortalTicketsPage } from '@/pages/portal/PortalTicketsPage.js';
import { PortalTicketDetailPage } from '@/pages/portal/PortalTicketDetailPage.js';
import { PortalNewTicketPage } from '@/pages/portal/PortalNewTicketPage.js';
import { PortalEstimatesPage } from '@/pages/portal/PortalEstimatesPage.js';
import { PortalInvoicesPage } from '@/pages/portal/PortalInvoicesPage.js';
import { PortalLoyaltyPage } from '@/pages/portal/PortalLoyaltyPage.js';
import { PortalProfilePage } from '@/pages/portal/PortalProfilePage.js';
import { PortalTokenViewPage } from '@/pages/portal/PortalTokenViewPage.js';
import { PublicTrackPage } from '@/pages/portal/PublicTrackPage.js';
import { EstimateApprovePage } from '@/pages/portal/EstimateApprovePage.js';
import { usePortalAuthStore } from '@/stores/portal-auth.js';

import { UserPreferencesProvider } from '@/contexts/UserPreferencesContext.js';

// Lazy-loaded pages (avoids stale HMR bindings after large App.tsx edits)
const TechDashboardPage = lazy(() =>
  import('@/pages/tech/TechDashboardPage.js').then((m) => ({ default: m.TechDashboardPage })),
);
const TechTicketPage = lazy(() =>
  import('@/pages/tech/TechTicketPage.js').then((m) => ({ default: m.TechTicketPage })),
);
const AppointmentsPage = lazy(() =>
  import('@/pages/appointments/AppointmentsPage.js').then((m) => ({ default: m.AppointmentsPage })),
);
const WalkInQueuePage = lazy(() =>
  import('@/pages/queue/WalkInQueuePage.js').then((m) => ({ default: m.WalkInQueuePage })),
);
const BookWidgetPage = lazy(() =>
  import('@/pages/book/BookWidgetPage.js').then((m) => ({ default: m.BookWidgetPage })),
);
const PortalAppointmentsPage = lazy(() =>
  import('@/pages/portal/PortalAppointmentsPage.js').then((m) => ({
    default: m.PortalAppointmentsPage,
  })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-500">
      Loading…
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <UserPreferencesProvider>{children}</UserPreferencesProvider>;
}

function PortalProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return <>{children}</>;
}

function RoleHomeRedirect() {
  const role = useAuthStore((s) => s.user?.role);
  if (!role) return <Navigate to="/login" replace />;
  return <Navigate to={postLoginPath(role)} replace />;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[App Error]', error, info);
    const stack = info.componentStack;
    if (stack !== undefined) {
      captureReactError(error, { componentStack: stack });
    } else {
      captureReactError(error);
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
          <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-xl font-bold text-slate-950">Something went wrong.</h1>
            <p className="mt-2 text-sm text-slate-600">Please refresh or contact support.</p>
            <button
              type="button"
              className="mt-5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function SentryUserBridge() {
  const user = useAuthStore((s) => s.user);
  useEffect(() => {
    if (user?.id) {
      setSentryUser({
        id: user.id,
        tenantId: user.tenantId,
        storeId: user.storeId,
      });
    } else {
      setSentryUser(null);
    }
  }, [user?.id, user?.tenantId, user?.storeId]);
  return null;
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <SentryUserBridge />
        <Toaster position="top-right" />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Photo capture — standalone, handles own auth */}
        <Route path="/photo" element={<PhotoCapturePage />} />

        {/* Public booking widget — no auth */}
        <Route path="/book/:storeId" element={<BookWidgetPage />} />

        {/* Customer-facing counter display — no auth */}
        <Route path="/display/:storeId" element={<DisplayPage />} />
        {/* Waiting-area queue TV — independent of counter display, no auth */}
        <Route path="/queue-display/:storeId" element={<QueueDisplayPage />} />

        {/* Public customer portal / tracker */}
        <Route path="/portal/t/:token" element={<PortalTokenViewPage />} />
        <Route path="/track" element={<PublicTrackPage />} />
        <Route path="/estimates/approve/:token" element={<EstimateApprovePage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
        <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />

        <Route
          path="/portal"
          element={
            <PortalProtectedRoute>
              <PortalLayout />
            </PortalProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboardPage />} />
          <Route path="tickets" element={<PortalTicketsPage />} />
          <Route path="tickets/new" element={<PortalNewTicketPage />} />
          <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
          <Route path="invoices" element={<PortalInvoicesPage />} />
          <Route path="estimates" element={<PortalEstimatesPage />} />
          <Route path="loyalty" element={<PortalLoyaltyPage />} />
          <Route path="appointments" element={<PortalAppointmentsPage />} />
          <Route path="profile" element={<PortalProfilePage />} />
          <Route path="profile/change-password" element={<PortalChangePasswordPage />} />
        </Route>

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
        </Route>

        {/* Protected app routes */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route index element={<RoleHomeRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/tech" element={<TechDashboardPage />} />
          <Route path="/tech/tickets/:id" element={<TechTicketPage />} />

          {/* Tickets */}
          <Route path="/tickets" element={<TicketQueuePage />} />
          <Route path="/tickets/quick" element={<QuickTicketPage />} />
          <Route path="/tickets/new" element={<NewTicketPage />} />
          <Route path="/tickets/:id/waiver/:waiverType/record" element={<TicketWaiverRecordPage />} />
          <Route path="/tickets/:id/checkout" element={<CheckoutPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />

          <Route path="/inquiries" element={<InquiriesListPage />} />
          <Route path="/inquiries/new" element={<NewInquiryPage />} />
          <Route path="/inquiries/:id" element={<InquiryDetailPage />} />

          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/queue" element={<WalkInQueuePage />} />

          <Route path="/messages" element={<MessagesPage />} />

          {/* POS */}
          <Route path="/pos" element={<POSPage />} />
          <Route path="/sales" element={<SalesHistoryPage />} />

          {/* Customers */}
          <Route path="/customers" element={<CustomerListPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />

          {/* Inventory — static paths before :id */}
          <Route path="/inventory/parts-requests" element={<PartsRequestsPage />} />
          <Route path="/inventory/rmas/new" element={<RmaCreatePage />} />
          <Route path="/inventory/rmas/:id" element={<RmaDetailPage />} />
          <Route path="/inventory/rmas" element={<RmaListPage />} />
          <Route path="/inventory/damage-reports" element={<DamageReportsPage />} />
          <Route path="/inventory/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
          <Route path="/inventory/purchase-orders" element={<PurchaseOrderListPage />} />
          <Route path="/inventory/counts/:id" element={<CycleCountDetailPage />} />
          <Route path="/inventory/counts" element={<CycleCountListPage />} />
          <Route path="/inventory/transfers/:id" element={<TransferDetailPage />} />
          <Route path="/inventory/transfers" element={<TransferListPage />} />
          <Route path="/inventory/bulk" element={<InventoryBulkPage />} />
          <Route path="/inventory/new" element={<InventoryItemPage />} />
          <Route path="/inventory/:id" element={<InventoryItemPage />} />
          <Route path="/inventory" element={<InventoryListPage />} />

          {/* Reports */}
          <Route path="/reports" element={<ReportsLayout />}>
            <Route index element={<ReportsDashboardPage />} />
            <Route path="revenue" element={<RevenueReportPage />} />
            <Route path="outstanding-balances" element={<OutstandingBalancesReportPage />} />
            <Route path="tickets" element={<TicketsReportPage />} />
            <Route path="technicians" element={<TechniciansReportPage />} />
            <Route path="repairs" element={<RepairsReportPage />} />
            <Route path="inventory" element={<InventoryReportPage />} />
            <Route path="multi-store" element={<MultiStoreReportPage />} />
            <Route path="end-of-day" element={<EndOfDayReportPage />} />
            <Route path="nps" element={<NpsReportPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="profit-loss" element={<ProfitLossReportPage />} />
            <Route path="customer-lifetime-value" element={<CustomerLtvReportPage />} />
            <Route path="acquisition" element={<AcquisitionReportPage />} />
            <Route path="cash-flow" element={<CashFlowReportPage />} />
            <Route path="parts-forecast/:reportId" element={<PartsForecastReportPage />} />
            <Route path="parts-forecast" element={<PartsForecastReportPage />} />
            <Route path="referrals" element={<ReferralsReportPage />} />
            <Route path="insurance" element={<InsuranceReportPage />} />
            <Route path="upsell" element={<UpsellReportPage />} />
            <Route path="discounts" element={<DiscountReportPage />} />
            <Route path="tips" element={<TipsReportPage />} />
            <Route path="walk-in-queue" element={<WalkInQueueReportPage />} />
            <Route path="rma-and-rework" element={<RmaAndReworkReportPage />} />
            <Route path="parts-requests" element={<PartsRequestReportPage />} />
          </Route>

          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />

          {/* Settings */}
          <Route path="/settings/*" element={<SettingsPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
